import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useFont } from "../../contexts/FontContext/FontContext";
import { useLogo } from "../../contexts/LogoContext/LogoContext";
import LogoWrapper from "../LogoWrapper/LogoWrapper";
import PortalOrderFetcher from "./PortalOrderFetcher";
import FlausFlosserForm from "../FlausFlosserForm/FlausFlosserForm";
import FlausLandingScreen from "../FlausLandingScreen/FlausLandingScreen";
import styles from "./Portal.module.css";

const Portal = ({ portalType = null }) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const [orderId, setOrderId] = useState("");
  const [uniqueOrderId, setUniqueOrderId] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [loadingOrderLookUp, setLoadingOrderLookUp] = useState(false);
  const [businessName, setBusinessName] = useState(null);
  const [businessLogo, setBusinessLogo] = useState(null);
  const [orderLookUpError, setOrderLookUpError] = useState(null);
  const [orderFound, setOrderFound] = useState(false);
  const [returnOrExchangeSelected, setReturnOrExchangeSelected] = useState(false);
  const [selectedExchangeOrder, setSelectedExchangeOrder] = useState("");
  const [showFlausLanding, setShowFlausLanding] = useState(false);
  const [showFlausForm, setShowFlausForm] = useState(false);
  const [flausInitialData, setFlausInitialData] = useState(null);
  const fontSchemes = useFont();
  const fontFamilyOne = fontSchemes.scheme_one;
  const logoUrl = useLogo();
  const isSubmitDisabled = !orderId.trim() || !zipCode.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitDisabled || !businessName) return;
    setLoadingOrderLookUp(true);
    setOrderLookUpError(null);

    const pathParts = window.location.pathname.split("/").filter(Boolean);
    const slug = decodeURIComponent(pathParts[0] || "").toLowerCase();
    const isFlaus = slug === "flaus";

    try {
      const response = await fetch(`${apiUrl}/api/portal_lookup/`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ order_id: orderId.trim(), zip_code: zipCode.trim(), business: businessName }) });
      const data = await response.json();

      if (isFlaus) {
        if (data.exists && data.customer) {
          setFlausInitialData({ orderId: orderId.trim(), ...data.customer });
        } else {
          setFlausInitialData({ orderId: orderId.trim() });
        }
        setShowFlausForm(true);
        return;
      }

      // ── unchanged flow for all other businesses ──
      if (data.error) setOrderLookUpError(data.error);
      if (data.exists) { setOrderFound(true); }
    } catch (err) {
      if (isFlaus) {
        setFlausInitialData({ orderId: orderId.trim() });
        setShowFlausForm(true);
        return;
      }
      console.log("Portal lookup error:", err);
      setOrderLookUpError("We couldn't find that order. Please double-check and try again.");
    } finally { setLoadingOrderLookUp(false); }
  };

  useEffect(() => {
    const fetchBusinessLogo = async () => {
      try {
        const pathParts = window.location.pathname.split("/").filter(Boolean);
        const rawSlug = pathParts[0];
        if (!rawSlug) return;
        const slug = decodeURIComponent(rawSlug);
        setBusinessName(slug);
        if (slug.toLowerCase() === "flaus") {
          setShowFlausLanding(true);
        }
        const response = await fetch(`${apiUrl}/api/get_business_logo_portal/?business=${encodeURIComponent(slug)}`, { method: "GET", headers: { "Content-Type": "application/json" } });
        const data = await response.json();
        setBusinessLogo(data.logo_filename);
      } catch (err) { console.log("orderLookUpError fetching business logo:", err); }
    };
    fetchBusinessLogo();
  }, [apiUrl]);

  const resetPortal = () => {
    setOrderFound(false);
    setOrderId("");
    setZipCode("");
    setOrderLookUpError(null);
  };

  return (
    <>
      <div className={`${styles.portal_container} ${fontFamilyOne}`}>
        <Helmet><title>Portal</title></Helmet>

        {/* ── Flaus landing screen (shown first for Flaus only) ── */}
        {showFlausLanding && !showFlausForm && (
          <FlausLandingScreen
            businessLogo={businessLogo}
            onFlausWebsite={() => setShowFlausLanding(false)}
            onOther={() => {
              setFlausInitialData({});
              setShowFlausForm(true);
              setShowFlausLanding(false);
            }}
          />
        )}

        {/* ── Flaus flosser form (after lookup or direct from "Other") ── */}
        {showFlausForm && (
          <FlausFlosserForm
            initialData={flausInitialData}
            businessName={businessName}
            businessLogo={businessLogo}
          />
        )}

        {/* ── Standard flow for all other businesses (unchanged) ── */}
        {!showFlausLanding && !showFlausForm && (
          <>
            {businessLogo && (<LogoWrapper src={`${process.env.PUBLIC_URL}/business_images/${businessLogo}`} class_={styles.logo_wrapper} />)}
            {orderFound && orderId && !returnOrExchangeSelected && (<h3 className={styles.chosen_order_id}>Order selected: #{orderId}</h3>)}
            {!orderFound && (
              <>
                <p className={`titles ${styles.portal_title}`}>Look up your order.</p>
                <form className={styles.portal_form} onSubmit={handleSubmit}>
                  <div className={styles.input_group}>
                    <label className={styles.input_label}>Order ID #</label>
                    <input className={styles.input_field} type="text" value={orderId} onChange={(e) => setOrderId(e.target.value)} placeholder="Enter your order ID" />
                  </div>
                  <div className={styles.input_group}>
                    <label className={styles.input_label}>ZIP Code</label>
                    <input className={styles.input_field} type="text" value={zipCode} onChange={(e) => setZipCode(e.target.value)} placeholder="Enter your ZIP code" />
                  </div>
                  <button type="submit" className={`${styles.submit_button} ${isSubmitDisabled ? styles.submit_button_disabled : ""}`} disabled={isSubmitDisabled}>{loadingOrderLookUp ? "Looking up..." : "Submit"}</button>
                </form>
                {orderLookUpError && <p className={styles.error_message}>Invalid entry.</p>}
              </>
            )}
            {orderFound && (
              <>
                {!returnOrExchangeSelected ? (
                  <div className={styles.action_card}>
                    <p className={styles.success_title}>What would you like to do?</p>
                    <div className={styles.action_row}>
                      {portalType !== "exchange" && (
                        <button className={styles.action_button} onClick={() => setReturnOrExchangeSelected("return")}>
                          <img src={`${process.env.PUBLIC_URL}/images/portal_return_image.png`} alt="Portal Return Image" className={styles.action_icon} />
                          <p className={styles.action_text}>Start a return</p>
                        </button>
                      )}
                      {portalType !== "return" && (
                        <button className={styles.action_button} onClick={() => setReturnOrExchangeSelected("exchange")}>
                          <img src={`${process.env.PUBLIC_URL}/images/portal_exchange_image.png`} alt="Portal Exchange Image" className={styles.action_icon} />
                          <p className={styles.action_text}>Exchange items</p>
                        </button>
                      )}
                    </div>
                    <button className={styles.secondary_button} onClick={resetPortal}>Look up a different order</button>
                  </div>
                ) : (<PortalOrderFetcher orderId={orderId} uniqueOrderId={uniqueOrderId} linkType={returnOrExchangeSelected} businessName={businessName} fontFamily={fontFamilyOne} />)}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default Portal;
